<template>
  <div class="infinite-time-picker">
    <div class="infinite-time-picker__frame">
      <wheel-column
        :values="hours"
        :value="hour"
        @change="onHourChange"
      />
      <span class="infinite-time-picker__sep">:</span>
      <wheel-column
        :values="minutes"
        :value="minute"
        @change="onMinuteChange"
      />
      <div class="infinite-time-picker__highlight" aria-hidden="true" />
      <div class="infinite-time-picker__fade infinite-time-picker__fade--top" aria-hidden="true" />
      <div class="infinite-time-picker__fade infinite-time-picker__fade--bottom" aria-hidden="true" />
    </div>
  </div>
</template>

<script>
import WheelColumn from './WheelColumn.vue';
import { buildRange, formatTime, normalizeTime, parseTime } from './utils';

export default {
  name: 'InfiniteTimePicker',

  components: {
    WheelColumn,
  },

  props: {
    value: {
      type: String,
      required: true,
    },
    minuteInterval: {
      type: Number,
      required: true,
    },
    availableTimes: {
      type: Array,
      default: null,
    },
  },

  computed: {
    slots() {
      if (!this.availableTimes || !this.availableTimes.length) {
        return null;
      }

      return this.availableTimes.map((time) => normalizeTime(time));
    },

    hours() {
      if (!this.slots) {
        return buildRange(0, 23, 1);
      }

      return [...new Set(this.slots.map((slot) => parseTime(slot).hour))].sort((a, b) => a - b);
    },

    minutes() {
      if (!this.slots) {
        return buildRange(0, 59, this.minuteInterval);
      }

      return [...new Set(
        this.slots
          .filter((slot) => parseTime(slot).hour === this.hour)
          .map((slot) => parseTime(slot).minute),
      )].sort((a, b) => a - b);
    },

    hour() {
      return parseTime(this.value).hour;
    },

    minute() {
      return parseTime(this.value).minute;
    },
  },

  watch: {
    availableTimes() {
      this.ensureValidValue();
    },
  },

  mounted() {
    this.ensureValidValue();
  },

  methods: {
    ensureValidValue() {
      if (!this.slots || !this.slots.length) {
        return;
      }

      if (this.slots.includes(normalizeTime(this.value))) {
        return;
      }

      this.$emit('input', this.slots[0]);
    },

    resolveMinute(hour, minute) {
      const validMinutes = this.slots
        ? [...new Set(
          this.slots
            .filter((slot) => parseTime(slot).hour === hour)
            .map((slot) => parseTime(slot).minute),
        )].sort((a, b) => a - b)
        : this.minutes;

      if (!validMinutes.length) {
        return minute;
      }

      if (validMinutes.includes(minute)) {
        return minute;
      }

      return validMinutes[0];
    },

    emitTime(hour, minute) {
      this.$emit('input', formatTime(hour, this.resolveMinute(hour, minute)));
    },

    onHourChange(hour) {
      this.emitTime(hour, this.minute);
    },

    onMinuteChange(minute) {
      this.emitTime(this.hour, minute);
    },
  },
};
</script>

<style scoped>
.infinite-time-picker__frame {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: fit-content;
  margin: 0 auto;
}

.infinite-time-picker__sep {
  font-size: 20px;
  line-height: 36px;
  color: #111827;
  padding-bottom: 2px;
}

.infinite-time-picker__highlight {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 36px;
  transform: translateY(-50%);
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  pointer-events: none;
}

.infinite-time-picker__fade {
  position: absolute;
  left: 0;
  right: 0;
  height: 72px;
  pointer-events: none;
}

.infinite-time-picker__fade--top {
  top: 0;
  background: linear-gradient(to bottom, #fff 20%, rgba(255, 255, 255, 0));
}

.infinite-time-picker__fade--bottom {
  bottom: 0;
  background: linear-gradient(to top, #fff 20%, rgba(255, 255, 255, 0));
}
</style>
